import React, { useState, useEffect, useCallback } from 'react';
import { Switch, Pagination, Input, Modal, Button, Form, Upload, Spin, Menu, Dropdown } from 'antd';
import { SearchOutlined, EyeFilled, DeleteFilled, MenuUnfoldOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getUsers, deleteUser, deActivateUser, addUser, verifyUser, downloadCsv, mapUserToTopSport, disableUserToTopSport } from '../../store/slices/userSlice';
import { CustomModal, SortableTableHeader } from '../../components/common';
import { requiredRule } from '../../utils';
import * as constant from '../../actions/constant';
import { generateCsv, download, mkConfig } from 'export-to-csv';
import Papa from 'papaparse';
import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';

const { confirm } = Modal;

// TypeScript interfaces
interface UserRole { rolename: string; }
interface UserData {
  _id: string;
  name: string;
  email: string;
  country?: string;
  isTopSportUser?: boolean;
  isActive?: boolean;
  isVerified?: boolean;
  role?: UserRole;
}

interface GetUsersParams {
  search_string: string;
  page: number;
  sortValue: string;
  sortOrder: number | string;
  emails?: string[];
}

// Static configurations
const CSV_CONFIG = {
  fieldSeparator: ',',
  quoteStrings: true,
  decimalSeparator: '.',
  useBom: true,
  useKeysAsHeaders: true,
  filename: 'users',
};

const TABLE_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'email', label: 'Email', sortable: false },
  { key: 'country', label: 'Country', sortable: false },
  { key: 'topSport', label: 'TopSport', sortable: false },
  { key: 'status', label: 'Status', sortable: false },
  { key: 'action', label: 'Action', sortable: false },
];

const FORM_FIELDS = [
  { name: 'name', label: 'User Name', placeholder: 'User Name' },
  { name: 'email', label: 'Email Id', placeholder: 'Email Id' },
  { name: 'password', label: 'Password', placeholder: 'Password' },
];

// Helper function
const getBase64 = (img: RcFile, callback: (url: string) => void): void => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

const Users: React.FC = () => {
  const dispatch = useAppDispatch();
  const { userData, totalCount, csvData, loading: reduxLoading } = useAppSelector((state) => state.user);

  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(0);
  const [searchString, setSearchString] = useState('');
  const [sortOrder, setSortOrder] = useState<number | ''>('');
  const [visible, setVisible] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [fileObj, setFileObj] = useState<RcFile | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback((params: GetUsersParams) => {
    dispatch(getUsers(params as any));
  }, [dispatch]);

  const getParams = useCallback((overrides = {}): GetUsersParams => ({
    search_string: searchString,
    page: currentPage,
    sortValue: '',
    sortOrder,
    ...overrides,
  }), [searchString, currentPage, sortOrder]);

  useEffect(() => {
    fetchUsers(getParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (csvData?.length > 0) {
      const csvConfig = mkConfig(CSV_CONFIG);
      const exportData = csvData.map(({ _id, ...rest }: any) => rest);
      download(csvConfig)(generateCsv(csvConfig)(exportData));
      setLoading(false);
    }
  }, [csvData]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchString(value);
    fetchUsers(getParams({ search_string: value }));
  };

  const handlePageChange = (page: number) => {
    const newPage = page - 1;
    setCurrentPage(newPage);
    fetchUsers(getParams({ page: newPage }));
  };

  const handleSort = (sortVal: string) => {
    const newOrder = sortOrder === -1 ? 1 : -1;
    setSortOrder(newOrder);
    fetchUsers(getParams({ sortValue: sortVal, sortOrder: newOrder }));
  };

  const showConfirm = (title: string, action: () => void) => {
    confirm({ title, content: '', onOk: action });
  };

  const handleDeactivate = (userId: string) => dispatch(deActivateUser({ userId }));

  const handleChange = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'done' && info.file.originFileObj) {
      getBase64(info.file.originFileObj, (url) => {
        setImageUrl(url);
        setFileObj(info.file.originFileObj as RcFile);
      });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setVisible(false);
      const formData = new FormData();
      Object.entries(values).forEach(([key, val]) => formData.append(key, val as string));
      if (fileObj) formData.append('file', fileObj);
      dispatch(addUser(formData));
      form.resetFields();
      setImageUrl('');
      setFileObj(null);
    } catch {
      // Validation failed - form will show errors
    }
  };

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
    setImageUrl('');
    setFileObj(null);
  };

  const handleUploadCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      complete: ({ data }) => {
        const emails = (data as { email?: string }[]).map((i) => i.email).filter(Boolean) as string[];
        if (emails.length) {
          setLoading(true);
          dispatch(mapUserToTopSport({ ...getParams(), emails }));
        }
      },
    });
  };

  const menu = (
    <Menu>
      <Menu.Item key="disable" onClick={() => dispatch(disableUserToTopSport({}))}>Disable User</Menu.Item>
      <Menu.Item key="sample"><a href="/sample_file.csv" target="_blank" className="clr-inhrit">Sample File</a></Menu.Item>
      <Menu.Item key="map">
        <label className="custom-file-uploaded mb-0">
          <input className="map-users" type="file" name="file" onChange={handleUploadCsv} />
          Map Users to TopSport
        </label>
      </Menu.Item>
      <Menu.Item key="export" onClick={() => { setLoading(true); dispatch(downloadCsv()); }}>Export CSV</Menu.Item>
    </Menu>
  );

  const renderUserRow = (user: UserData) => {
    if (user.role?.rolename !== 'user' && user.role?.rolename !== 'kingbot') return null;
    return (
      <tr key={user._id}>
        <td>{user.name}</td>
        <td>{user.email}</td>
        <td>{user.country}</td>
        <td>{user.isTopSportUser ? 'Yes' : 'No'}</td>
        <td><Switch checked={user.isActive} onChange={() => handleDeactivate(user._id)} /></td>
        <td className="action-icons">
          <Link to={`/userprofile/${user._id}`}>
            <Button type="link" className="btn-sm mr-2"><EyeFilled style={{ color: '#666' }} /></Button>
          </Link>
          {user.role?.rolename === 'user' && (
            <Button type="link" className="btn-sm mr-2" onClick={() => showConfirm(constant.DELETE_RECORD, () => dispatch(deleteUser({ userId: user._id })))}>
              <DeleteFilled style={{ color: '#666' }} />
            </Button>
          )}
          {!user.isVerified && (
            <Button type="link" className="btn-sm" onClick={() => showConfirm(constant.VERIFY_RECORD, () => dispatch(verifyUser({ userId: user._id })))}>
              <i className="fa fa-envelope" style={{ color: '#666' }} aria-hidden="true" />
            </Button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <Spin spinning={loading || reduxLoading} delay={500}>
      <h2 className="page-heading">Manage User</h2>
      <div className="box box-default mb-4">
        <div className="box-body">
          <Row className="mb-4">
            <Col xs={9} sm={6}>
              <Input prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} placeholder="Search" onChange={handleSearch} style={{ width: '100%' }} />
            </Col>
            <Col xs={3} sm={6} className="text-right">
              <Dropdown overlay={menu} trigger={['click']}>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a className="muser-dropdown-link" href="#" onClick={(e) => e.preventDefault()}><MenuUnfoldOutlined /></a>
              </Dropdown>
            </Col>
          </Row>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  {TABLE_COLUMNS.map(({ key, label, sortable }) =>
                    sortable ? (
                      <SortableTableHeader key={key} label={label} sortKey={key} currentSortOrder={sortOrder} onSort={handleSort} />
                    ) : (
                      <th key={key} scope="col">{label}</th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>{userData.map(renderUserRow)}</tbody>
            </table>
          </div>
          <Pagination style={{ textAlign: 'right' }} pageSize={10} current={currentPage + 1} onChange={handlePageChange} total={totalCount || 1} />
          <CustomModal handleCancel={handleCancel} handleOk={handleOk} visible={visible} title="User" backBtnText="Back" submitBtnText="Submit">
            <Form form={form} className="team-form l_hgt0" layout="vertical">
              <Upload name="avatar" listType="picture-card" className="avatar-uploader" showUploadList={false} action="https://www.mocky.io/v2/5cc8019d300000980a055e76" onChange={handleChange}>
                <img src={imageUrl || 'http://placehold.it/380x500'} alt="profile" className="img-rounded img-responsive img-104" />
              </Upload>
              {FORM_FIELDS.map(({ name, label, placeholder }) => (
                <Form.Item key={name} label={label} name={name} rules={[requiredRule(label)]}>
                  <Input placeholder={placeholder} />
                </Form.Item>
              ))}
            </Form>
          </CustomModal>
        </div>
      </div>
    </Spin>
  );
};

export default Users;
